/**
 * dd-card-rating-hydrate.js
 *
 * Two enhancers for the expanded product card (`.dd-card2`):
 *
 * 1. Rating hydration — fills the rating pill when Shopify metafields
 *    (`reviews.rating` / `loox.avg_rating`) are empty, by batching a
 *    single request to the Klaviyo aggregates edge function.
 *
 *      <div class="dd-card2__rating-slot" data-needs-rating="999" hidden>
 *
 *    The API URL is read from the nearest ancestor carrying
 *    `data-reviews-api-url` (set by sections/related-products.liquid).
 *
 * 2. Swatch-overflow expand — first 5 swatches are visible; clicking
 *    the "+N" chip expands the rail into a horizontal scroll.
 *
 *      <div class="dd-card2__swatches" data-swatch-scroll> …
 *        <button class="dd-card2__swatch-more" data-swatch-more>+ 28</button>
 *      </div>
 */
(() => {
  const SELECTOR = '[data-needs-rating]';

  function initSwatchExpand() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-swatch-more]');
      if (!trigger) return;
      const rail = trigger.closest('[data-swatch-scroll]');
      if (!rail) return;
      event.preventDefault();
      rail.classList.add('is-expanded');
      trigger.setAttribute('aria-expanded', 'true');
      // After expansion the rail is scrollable — scroll the first
      // previously-hidden swatch into view so users see there's more.
      const firstHidden = rail.querySelector('.dd-card2__swatch:nth-child(n + 6)');
      if (firstHidden && typeof firstHidden.scrollIntoView === 'function') {
        firstHidden.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    });
  }

  function init() {
    initSwatchExpand();

    const slots = Array.from(document.querySelectorAll(SELECTOR));
    if (slots.length === 0) return;

    // Use the first slot to derive the API origin — every slot shares the
    // same ancestor section, so the URL is identical.
    const host = slots[0].closest('[data-reviews-api-url]');
    const apiUrl = host?.getAttribute('data-reviews-api-url') || '';
    const proxyToken = host?.getAttribute('data-reviews-proxy-token') || '';
    if (!apiUrl) return;

    const ids = Array.from(
      new Set(slots.map((el) => el.getAttribute('data-needs-rating')).filter(Boolean))
    ).slice(0, 20);
    if (ids.length === 0) return;

    const u = new URL(apiUrl, window.location.origin);
    // Point at the sibling aggregates endpoint on the same Vercel project.
    // Accepts both `/api/reviews` (list endpoint, default) and plain host URLs.
    u.pathname = u.pathname.replace(/\/api\/reviews\/?$/, '/api/reviews-aggregates');
    if (!/\/api\/reviews-aggregates$/.test(u.pathname)) {
      u.pathname = u.pathname.replace(/\/+$/, '') + '/api/reviews-aggregates';
    }
    u.searchParams.set('product_ids', ids.join(','));

    const proxyFetcher = window.DDKlaviyoReview?.fetchReviewsProxy;
    let request;
    if (proxyFetcher) {
      request = proxyFetcher(u.toString(), proxyToken, { credentials: 'omit' });
    } else {
      // Collection pages do not always load the shared review core. Keep the
      // fallback self-contained and use native fetch's actual two-argument
      // signature instead of passing the proxy helper's three arguments.
      const headers = {};
      if (proxyToken) headers['X-DD-Reviews-Token'] = proxyToken;

      if (/^dd-klaviyo-reviews-[a-z0-9]+-drop-dead\.vercel\.app$/i.test(u.hostname)) {
        const bypass = proxyToken.replace(/[^a-z0-9]/gi, '').slice(0, 32);
        if (bypass.length === 32) {
          u.searchParams.set('x-vercel-protection-bypass', bypass);
        }
      }

      request = fetch(u.toString(), { credentials: 'omit', headers });
    }

    request
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        const aggregates = payload?.aggregates || {};
        slots.forEach((slot) => {
          const pid = slot.getAttribute('data-needs-rating');
          const agg = aggregates[pid];
          if (!agg || !agg.count || !agg.avg) return;
          renderRating(slot, agg.avg);
        });
      })
      .catch(() => {
        // Silent failure — card just stays without a rating pill, which
        // matches the "no metafield, no rating" baseline.
      });
  }

  function renderRating(slot, avg) {
    const safe = Number(avg).toFixed(1);
    slot.outerHTML = `
      <div class="dd-card2__rating" aria-label="Rated ${safe} out of 5">
        <svg class="dd-card2__star" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2 15.09 8.26 22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2Z"/>
        </svg>
        <span class="dd-card2__rating-value">${safe}</span>
      </div>
    `;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Re-run when additional cards are appended at runtime (e.g. a
  // collection grid's "Load more" button). `init` is idempotent:
  // hydrated slots lose the `data-needs-rating` hook so they're
  // skipped on subsequent passes.
  document.addEventListener('dd:cards:appended', init);
})();
